import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import {
  calculateVolume,
  calculateWeightFromVolume,
  getEffectiveWeight,
  calculateDaysBetween,
  calculateInterest,
  fetchExchangeRate,
  getShippingCost
} from './costSheetUtils';

const CostSheetForm = ({ 
  initialValues = {}, 
  onSubmit, 
  onCalculate,
  showResults = false 
}) => {
  const [formData, setFormData] = useState({
    inputCost: initialValues.inputCost || '',
    supplierName: initialValues.supplierName || '',
    model: initialValues.model || '',
    description: initialValues.description || '',
    quantity: initialValues.quantity || '',
    currency: initialValues.currency || 'USD',
    hsCode: initialValues.hsCode || '',
    length: initialValues.length || '',
    width: initialValues.width || '',
    height: initialValues.height || '',
    inputWeight: initialValues.inputWeight || '',
    shippingMode: initialValues.shippingMode || 'air',
    deliveryDays: initialValues.deliveryDays || '',
    paymentDate: initialValues.paymentDate || '',
    incoterm: initialValues.incoterm || 'exworks',
    margin: initialValues.margin || ''
  });

  const [hsCodes, setHsCodes] = useState([]);
  const [filteredHsCodes, setFilteredHsCodes] = useState([]);
  const [showHsCodeDropdown, setShowHsCodeDropdown] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(1);
  const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
  const [calculatedValues, setCalculatedValues] = useState({
    volume: 0,
    calculatedWeight: 0,
    effectiveWeight: 0,
    interestDays: 0,
    interestAmount: 0,
    shippingCost: 100
  });

  // Fetch HS codes from Firestore
  // Using getDocs instead of onSnapshot to avoid listener issues with non-existent collections
  useEffect(() => {
    let isMounted = true;
    
    const fetchHsCodes = async () => {
      try {
        const q = query(collection(db, 'hsCodes'));
        const snapshot = await getDocs(q);
        
        if (isMounted) {
          const codes = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setHsCodes(codes);
        }
      } catch (error) {
        console.error('Firestore error fetching HS codes:', error);
        if (isMounted) {
          setHsCodes([]);
        }
      }
    };

    fetchHsCodes();
    
    // Optionally refresh periodically if needed (every 5 minutes)
    const interval = setInterval(fetchHsCodes, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Fetch exchange rate when currency changes
  useEffect(() => {
    const fetchRate = async () => {
      if (formData.currency && formData.currency !== 'AED') {
        setExchangeRateLoading(true);
        const apiUrl = process.env.REACT_APP_EXCHANGE_RATE_API_URL;
        const rate = await fetchExchangeRate(formData.currency, 'AED', apiUrl);
        setExchangeRate(rate);
        setExchangeRateLoading(false);
      } else {
        setExchangeRate(1);
      }
    };
    fetchRate();
  }, [formData.currency]);

  // Calculate volume and weight when dimensions change
  useEffect(() => {
    if (formData.length && formData.width && formData.height) {
      const volume = calculateVolume(formData.length, formData.width, formData.height);
      const calculatedWeight = calculateWeightFromVolume(volume);
      const effectiveWeight = getEffectiveWeight(formData.inputWeight, calculatedWeight);
      
      setCalculatedValues(prev => ({
        ...prev,
        volume,
        calculatedWeight,
        effectiveWeight
      }));
    }
  }, [formData.length, formData.width, formData.height, formData.inputWeight]);

  // Calculate interest when dates change
  useEffect(() => {
    if (formData.deliveryDays && formData.paymentDate) {
      const today = new Date();
      const deliveryDate = new Date(today);
      deliveryDate.setDate(today.getDate() + parseInt(formData.deliveryDays));
      
      const paymentDate = new Date(formData.paymentDate);
      const interestDays = calculateDaysBetween(deliveryDate, paymentDate);
      const interestAmount = calculateInterest(formData.inputCost, interestDays);
      
      setCalculatedValues(prev => ({
        ...prev,
        interestDays,
        interestAmount
      }));
    }
  }, [formData.deliveryDays, formData.paymentDate, formData.inputCost]);

  // Calculate shipping cost
  useEffect(() => {
    const shippingCost = getShippingCost(formData.shippingMode);
    setCalculatedValues(prev => ({
      ...prev,
      shippingCost
    }));
  }, [formData.shippingMode]);

  // Filter HS codes based on input
  const handleHsCodeChange = (value) => {
    setFormData(prev => ({ ...prev, hsCode: value }));
    
    if (value.length > 0) {
      const filtered = hsCodes.filter(code => 
        code.code.toLowerCase().includes(value.toLowerCase()) ||
        (code.description && code.description.toLowerCase().includes(value.toLowerCase()))
      );
      setFilteredHsCodes(filtered);
      setShowHsCodeDropdown(true);
    } else {
      setShowHsCodeDropdown(false);
    }
  };

  const handleSelectHsCode = (code) => {
    setFormData(prev => ({ ...prev, hsCode: code.code }));
    setShowHsCodeDropdown(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCalculate = () => {
    if (onCalculate) {
      onCalculate({
        ...formData,
        ...calculatedValues,
        exchangeRate
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (onSubmit) {
      await onSubmit({
        ...formData,
        ...calculatedValues,
        exchangeRate
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-6">Basic Information</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Input Cost *</label>
            <input
              type="number"
              step="0.01"
              value={formData.inputCost}
              onChange={(e) => handleChange('inputCost', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Supplier Name *</label>
            <input
              type="text"
              value={formData.supplierName}
              onChange={(e) => handleChange('supplierName', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Model</label>
            <input
              type="text"
              value={formData.model}
              onChange={(e) => handleChange('model', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Quantity *</label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => handleChange('quantity', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-6">Currency & Exchange Rate</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Currency *</label>
            <select
              value={formData.currency}
              onChange={(e) => handleChange('currency', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            >
              <option value="USD">USD</option>
              <option value="SAR">SAR</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Exchange Rate (to AED)</label>
            <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50">
              {exchangeRateLoading ? (
                <span className="text-gray-500">Loading...</span>
              ) : (
                <span>{exchangeRate.toFixed(4)}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-6">Product Details</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <label className="block text-sm font-medium mb-2">HS CODE *</label>
            <input
              type="text"
              value={formData.hsCode}
              onChange={(e) => handleHsCodeChange(e.target.value)}
              onFocus={() => {
                if (formData.hsCode.length > 0) {
                  handleHsCodeChange(formData.hsCode);
                }
              }}
              maxLength={12}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            />
            {showHsCodeDropdown && filteredHsCodes.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredHsCodes.map((code) => (
                  <button
                    key={code.id}
                    type="button"
                    onClick={() => handleSelectHsCode(code)}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                  >
                    <div className="font-medium">{code.code}</div>
                    {code.description && (
                      <div className="text-sm text-gray-500">{code.description}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Input Weight (kg)</label>
            <input
              type="number"
              step="0.01"
              value={formData.inputWeight}
              onChange={(e) => handleChange('inputWeight', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Length (cm) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.length}
              onChange={(e) => handleChange('length', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Width (cm) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.width}
              onChange={(e) => handleChange('width', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Height (cm) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.height}
              onChange={(e) => handleChange('height', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Calculated Volume (cm³)</label>
            <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50">
              {calculatedValues.volume.toFixed(2)}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Calculated Weight (kg)</label>
            <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50">
              {calculatedValues.calculatedWeight.toFixed(2)}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Effective Weight (kg)</label>
            <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50 font-semibold">
              {calculatedValues.effectiveWeight.toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-6">Shipping & Delivery</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Mode of Shipping *</label>
            <select
              value={formData.shippingMode}
              onChange={(e) => handleChange('shippingMode', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            >
              <option value="air">Air</option>
              <option value="ship">Ship</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Shipping Cost (AED)</label>
            <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50">
              {calculatedValues.shippingCost.toFixed(2)}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Days of Delivery *</label>
            <input
              type="number"
              value={formData.deliveryDays}
              onChange={(e) => handleChange('deliveryDays', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Payment Date *</label>
            <input
              type="date"
              value={formData.paymentDate}
              onChange={(e) => handleChange('paymentDate', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            />
          </div>

          {calculatedValues.interestDays > 0 && (
            <>
              <div>
                <label className="block text-sm font-medium mb-2">Interest Days</label>
                <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50">
                  {calculatedValues.interestDays} days
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Interest Amount (AED)</label>
                <div className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-50">
                  {calculatedValues.interestAmount.toFixed(2)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold mb-6">Pricing</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Incoterm *</label>
            <select
              value={formData.incoterm}
              onChange={(e) => handleChange('incoterm', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            >
              <option value="exworks">Exworks</option>
              <option value="CIS">CIS</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Margin (%) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.margin}
              onChange={(e) => handleChange('margin', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              required
            />
          </div>
        </div>
      </div>

      {showResults && (
        <div className="flex gap-4">
          <button
            type="button"
            onClick={handleCalculate}
            className="flex-1 bg-gray-800 text-white px-6 py-3 rounded-md hover:bg-gray-900 transition-colors font-medium"
          >
            Recalculate
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 bg-black text-white px-6 py-3 rounded-md hover:bg-gray-800 transition-colors font-medium"
          >
            Save Cost Sheet
          </button>
        </div>
      )}

      {!showResults && (
        <button
          type="button"
          onClick={handleCalculate}
          className="w-full bg-black text-white px-6 py-3 rounded-md hover:bg-gray-800 transition-colors font-medium"
        >
          Calculate Price
        </button>
      )}
    </form>
  );
};

export default CostSheetForm;

